import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import {
  FileText,
  Search,
  BarChart3,
  MessageSquare,
  Upload,
  CheckCircle2,
  Zap,
  Shield,
  Users,
  ArrowRight,
  Coins,
  Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <Card className="border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all">
      <CardHeader className="pb-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 mb-2">
          {icon}
        </div>
        <CardTitle className="text-lg font-bold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}

interface StepProps {
  number: number;
  title: string;
  description: string;
}

function Step({ number, title, description }: StepProps) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="h-12 w-12 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-bold mb-4">
        {number}
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
    </div>
  );
}

interface PricingCardProps {
  credits: number;
  price: number;
  popular?: boolean;
}

function PricingCard({ credits, price, popular }: PricingCardProps) {
  const perCredit = (price / credits).toFixed(2);
  return (
    <Card
      className={
        popular
          ? 'border-indigo-600 shadow-lg ring-1 ring-indigo-600 relative'
          : 'border-slate-200'
      }
    >
      {popular && (
        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white">
          <Star className="h-3 w-3 mr-1" />
          Popular
        </Badge>
      )}
      <CardHeader className="text-center pb-2">
        <div className="flex items-center justify-center gap-1.5 mb-1">
          <Coins className="h-5 w-5 text-amber-500" />
          <CardTitle className="text-2xl font-bold text-slate-900">
            {credits}
          </CardTitle>
        </div>
        <p className="text-sm text-slate-500">credits</p>
      </CardHeader>
      <CardContent className="text-center space-y-3">
        <div>
          <span className="text-3xl font-bold text-slate-900">{price.toFixed(2)}€</span>
        </div>
        <p className="text-xs text-slate-500">{perCredit}€ per credit</p>
        <Link to="/register">
          <Button
            className={
              popular
                ? 'w-full bg-indigo-600 hover:bg-indigo-700 mt-2'
                : 'w-full mt-2'
            }
            variant={popular ? 'default' : 'outline'}
            size="sm"
          >
            Get Started
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

const features: FeatureCardProps[] = [
  {
    icon: <Upload className="h-5 w-5" />,
    title: 'Smart Upload',
    description:
      'Drag-and-drop PDF, DOCX, or TXT files. Import RIS/BIB bibliographies. Automatic text extraction and deduplication.',
  },
  {
    icon: <CheckCircle2 className="h-5 w-5" />,
    title: '5 Criteria Types',
    description:
      'Boolean, scale, text match, PICO, and bias risk criteria. Customize weights and use pre-built templates.',
  },
  {
    icon: <Zap className="h-5 w-5" />,
    title: 'AI Evaluation',
    description:
      'GPT-4 or Claude scores each source against your criteria with exact passage citations for full transparency.',
  },
  {
    icon: <BarChart3 className="h-5 w-5" />,
    title: 'Visual Results',
    description:
      'Score breakdowns, color-coded bars, Kanban board for batch results, and auto-bucketing by relevance.',
  },
  {
    icon: <MessageSquare className="h-5 w-5" />,
    title: 'Chat with PDF',
    description:
      'Ask questions about any document. Get answers with inline passage citations and highlighted source text.',
  },
  {
    icon: <Search className="h-5 w-5" />,
    title: 'Paper Search',
    description:
      'Search Semantic Scholar directly. Import results into your folders with a single click.',
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: 'Team Collaboration',
    description:
      'Invite reviewers, assign roles, run blinded screening, and resolve conflicts with built-in tools.',
  },
  {
    icon: <FileText className="h-5 w-5" />,
    title: 'PRISMA & Reports',
    description:
      'Auto-generate PRISMA flow diagrams and comprehensive review reports in PDF or DOCX format.',
  },
  {
    icon: <Shield className="h-5 w-5" />,
    title: 'Data Extraction',
    description:
      'Extract structured data into editable comparison tables. Export to CSV or Excel for further analysis.',
  },
];

const steps: StepProps[] = [
  {
    number: 1,
    title: 'Upload Sources',
    description: 'Drag-and-drop your documents or import from bibliography files.',
  },
  {
    number: 2,
    title: 'Define Criteria',
    description: 'Set up evaluation criteria with custom weights and types.',
  },
  {
    number: 3,
    title: 'Run Evaluation',
    description: 'AI scores each source with passage-level citations.',
  },
  {
    number: 4,
    title: 'Review Results',
    description: 'Analyze scores, compare documents, and generate reports.',
  },
];

const pricingPlans: PricingCardProps[] = [
  { credits: 10, price: 4.99 },
  { credits: 50, price: 19.99, popular: true },
  { credits: 100, price: 34.99 },
  { credits: 500, price: 149.99 },
];

export function Landing() {
  const { session } = useAuthStore();
  if (session) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="py-20 md:py-28 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <Badge variant="secondary" className="text-sm px-3 py-1">
            Powered by GPT-4 & Claude
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 tracking-tight">
            Evaluate sources with{' '}
            <span className="text-indigo-600">AI precision</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Upload your documents, define custom criteria, and get AI-powered
            relevance scores with exact passage citations. Built for
            systematic reviews and research.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 gap-2">
                Start Free
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="gap-2">
                <Coins className="h-4 w-4" />
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-slate-50/50">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-bold text-slate-900">
              Everything you need for systematic reviews
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From document upload to final report — a complete toolkit for
              researchers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-bold text-slate-900">
              How it works
            </h2>
            <p className="text-slate-600">Four steps to actionable results.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step) => (
              <Step key={step.number} {...step} />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 px-4 bg-slate-50/50">
        <div className="max-w-4xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <h2 className="text-3xl font-bold text-slate-900">
              Simple credit-based pricing
            </h2>
            <p className="text-slate-600">
              Pay only for what you use. 1 credit = 1 AI evaluation.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {pricingPlans.map((plan) => (
              <PricingCard key={plan.credits} {...plan} />
            ))}
          </div>
          <div className="text-center">
            <Link to="/pricing">
              <Button variant="link" className="text-indigo-600 gap-1">
                Learn more about pricing
                <ArrowRight className="h-3 w-3" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold text-slate-900">
            Ready to streamline your research?
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto">
            Join researchers who use MakeMyDoc to evaluate hundreds of sources
            in minutes, not days.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 gap-2 mt-2">
              Get Started Free
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-600" />
            <span className="font-medium text-slate-700">MakeMyDoc</span>
          </div>
          <p>&copy; {new Date().getFullYear()} MakeMyDoc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
